import * as React from 'react';

import { RecommendationDesktop, RecommendationDesktopSkeleton } from '../../components/RedesignedRecommendationDesktop';
import {
  newbuildingRecommendationsRowClassName,
  RecommendationsListDesktop,
  RecommendationsListDesktopWithNewbuildings,
} from '../../components/RedesignedRecommendationsListDesktop';
import { EPaginationStatus } from '../../slices/recommendationsSlice';
import { ELoadingStatus } from '../../types/loadingStatus';
import { RedesignedNewbuildingRecommendations } from '../NewbuildingRecommendations/RedesignedNewbuildingRecommendations';
import { RedesignedFavoriteButtonDesktopContainer } from '../RedesignedFavoriteButtonDesktopContainer';
import { RedesignedLabelsContainer } from '../RedesignedLabelsContainer';
import { useInfiniteRecommendations } from './useInfiniteRecommendations';
import type { useRecommendations } from './useRecommendations';
import * as styles from './RedesignedRecommendationsListDesktopContainer.css';

const PAGINATION_PAGE_SIZE = 8;

type TTrackRecommendationShow = (offerId: number | null, position: number, contextToken?: string) => void;

type TRedesignedRecommendationsListDesktopContainerProps = {
  recommendationsControls: ReturnType<typeof useRecommendations>;
  onTrack: TTrackRecommendationShow;
  showNewbuildingRecommendations: boolean;
};

export const RedesignedRecommendationsListDesktopContainer: React.FC<
  TRedesignedRecommendationsListDesktopContainerProps
> = ({ recommendationsControls, onTrack, showNewbuildingRecommendations }) => {
  const {
    recommendations,
    onRecommendationClick,
    onShow,
    onShowTrap,
    recommendationsStatus,
    regionId,
    hasMore,
    recommendationsTotalCount,
    paginationStatus,
    onLoadMore,
    isOfferVisited,
    showFullList,
  } = recommendationsControls;

  const desktopSkeletonCount = showFullList ? 40 : 8;

  const recommendationsDesktopSkeleton = React.useMemo(
    () =>
      Array.from({ length: desktopSkeletonCount }, (_, index) => (
        <RecommendationDesktopSkeleton key={`redesigned-recommendation-desktop-skeleton-${index}`} />
      )),
    [desktopSkeletonCount],
  );

  const paginationSkeletonCount = React.useMemo(() => {
    const remainingRecommendationsCount =
      recommendationsTotalCount === null
        ? PAGINATION_PAGE_SIZE
        : Math.max(0, recommendationsTotalCount - recommendations.length);

    return Math.min(PAGINATION_PAGE_SIZE, remainingRecommendationsCount);
  }, [recommendations.length, recommendationsTotalCount]);

  const recommendationsDesktopPaginationSkeleton = React.useMemo(
    () =>
      Array.from({ length: paginationSkeletonCount }, (_, index) => (
        <RecommendationDesktopSkeleton key={`redesigned-recommendation-desktop-pagination-skeleton-${index}`} />
      )),
    [paginationSkeletonCount],
  );

  const { sentinelRef } = useInfiniteRecommendations({
    onShow,
    recommendations,
    hasMore,
    paginationStatus,
    onLoadMore,
  });

  const isPaginationLoading = paginationStatus === EPaginationStatus.Loading;
  const showPaginationSentinel = hasMore && paginationStatus !== EPaginationStatus.Error;

  if (recommendationsStatus === ELoadingStatus.Loading) {
    return (
      <RecommendationsListDesktop showMoreLink={false}>{recommendationsDesktopSkeleton}</RecommendationsListDesktop>
    );
  }

  if (!recommendations.length) {
    return null;
  }

  const recommendationCards = recommendations.map((recommendation, index) => (
    <RecommendationDesktop
      {...recommendation}
      onTrack={onTrack}
      position={index + 1}
      key={recommendation.offerId}
      onClick={onRecommendationClick}
      onShowTrap={onShowTrap}
      regionId={recommendation.regionId ?? regionId}
      favoriteButton={<RedesignedFavoriteButtonDesktopContainer id={recommendation.offerId} />}
      labels={<RedesignedLabelsContainer visited={isOfferVisited(recommendation.offerId)} />}
    />
  ));

  const paginationFooter = isPaginationLoading ? recommendationsDesktopPaginationSkeleton : null;
  const paginationSentinel = showPaginationSentinel ? (
    <div
      ref={sentinelRef}
      className={styles['pagination-sentinel']}
      data-name="RedesignedRecommendationsPaginationSentinel"
    />
  ) : null;

  if (!showNewbuildingRecommendations) {
    return (
      <RecommendationsListDesktop
        onShow={onShow}
        showMoreLink={false}
        footer={paginationFooter}
        afterList={paginationSentinel}
      >
        {recommendationCards}
      </RecommendationsListDesktop>
    );
  }

  return (
    <RecommendationsListDesktopWithNewbuildings
      showMoreLink={false}
      secondRow={<RedesignedNewbuildingRecommendations className={newbuildingRecommendationsRowClassName} />}
      onShow={onShow}
      footer={paginationFooter}
      afterList={paginationSentinel}
    >
      {recommendationCards}
    </RecommendationsListDesktopWithNewbuildings>
  );
};
